import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { StyledMember, StyledPanel } from '../stylesheets/styles';
import { API_URL } from '../common/constants';
import BgComponent from '../components/BgComponent';
import ModalContext from '../contexts/ModalContext';
import classNames from "classnames";
import { isEmpty } from 'lodash';

const MemberProfile = () => {
  const [memberInfo, setMemberInfo] = useState({});
  const [invoiceAddr, setInvoiceAddr] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', isSuccess: false });
  const { handleShow } = useContext(ModalContext);

  const showMessage = (text, isSuccess = false) => {
    setMessage({ text, isSuccess });
    setTimeout(() => setMessage({ text: '', isSuccess: false }), 5000);
  };

  const fetchMemberInfo = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      handleShow();
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/member`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 200 && response.data.isSuccess) {
        const data = response.data.result;
        setMemberInfo(data);
        setInvoiceAddr(data.invoiceAddr || '');
      } else {
        console.error('Failed to fetch member info:', response.data.message);
        showMessage('載入會員資料失敗', false);
      }
    } catch (error) {
      console.error('Error fetching member info:', error);
      showMessage('載入會員資料時發生錯誤', false);
      if (error.response?.status === 401) {
        handleShow();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    if (!token) {
      handleShow();
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API_URL}/api/member`, 
        { invoiceAddr },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 && response.data.isSuccess) {
        showMessage('發票地址更新成功', true);
        // 重新載入會員資料
        fetchMemberInfo();
      } else {
        showMessage(response.data.message || '更新失敗', false);
      }
    } catch (error) {
      console.error('Error updating member info:', error);
      showMessage('更新時發生錯誤', false);
      if (error.response?.status === 401) {
        handleShow();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getGenderText = (gender) => {
    const genderMap = {
      'M': '男',
      'F': '女',
      'U': '未指定'
    };
    return genderMap[gender] || gender || '--';
  };

  useEffect(() => {
    fetchMemberInfo();
  }, []);

  return (
    <StyledMember className='register'>
      <div className='member-wrap'>
        <Container>
          <Row className="justify-content-md-center">
            <Col md="12">
              <StyledPanel>
                <div className="hgroup">
                  <h1 className="text-center">資料修改</h1>
                </div>
                  
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">載入中...</span>
                      </div>
                    </div>
                  ) : (
                    <Form onSubmit={handleSubmit}>
                      {/* 會員基本資料 - 只顯示，不可編輯 */}
                      <div className="mb-4">
                        <h5 className="mb-3">會員基本資料</h5>
                        
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>姓名</Form.Label>
                              <Form.Control 
                                type="text" 
                                value={memberInfo.name || ''} 
                                readOnly 
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>性別</Form.Label>
                              <Form.Control 
                                type="text" 
                                value={getGenderText(memberInfo.gender)} 
                                readOnly 
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>電子郵件</Form.Label>
                              <Form.Control 
                                type="email" 
                                value={memberInfo.email || ''} 
                                readOnly 
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>手機號碼</Form.Label>
                              <Form.Control 
                                type="text" 
                                value={memberInfo.phoneNumber || ''} 
                                readOnly 
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>註冊日期時間</Form.Label>
                              <Form.Control 
                                type="text" 
                                value={formatDateTime(memberInfo.createTime)} 
                                readOnly 
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>最後登入日期時間</Form.Label>
                              <Form.Control 
                                type="text" 
                                value={formatDateTime(memberInfo.lastLoginTime)} 
                                readOnly 
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>

                      {/* 可編輯的發票地址 */}
                      <div className="mb-4">
                        <h5 className="mb-3">發票資訊</h5>
                        <Form.Group className="mb-3">
                          <Form.Label>發票地址</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={invoiceAddr}
                            onChange={(e) => setInvoiceAddr(e.target.value)}
                            placeholder="請輸入發票地址"
                          />
                        </Form.Group>
                      </div>

                      <div className="form-button pt-4">
                        <Button
                          disabled={submitting}
                          size="lg"
                          variant="dark"
                          className="w-100"
                          type="submit"
                        >
                          {submitting ? '更新中...' : '更新資料'}
                        </Button>
                        {message.text && (
                          <p className={classNames("form-helper mt-2", message.isSuccess ? 'text-success' : 'text-danger')}>
                            {message.text}
                          </p>
                        )}
                      </div>
                    </Form>
                  )}
              </StyledPanel>
            </Col>
          </Row>
        </Container>
        <BgComponent/>
      </div>
    </StyledMember>
  );
};

export default MemberProfile;